import { createContext, useContext } from 'react';
import { LANGS, type BrandSettings, type DataSource, type Lang, type ModuleKey, type PortalMe, pickText } from '@lp/shared';

export interface Session {
  me: PortalMe;
  /** null only if the settings request failed; pages fall back to the languages in `me`. */
  settings: BrandSettings | null;
  setSettings: (s: BrandSettings) => void;
  /** Enabled content languages, default language first (that tab opens first in every form). */
  langs: Lang[];
  defaultLang: Lang;
  /** Brand owners, and platform admins signed in to this brand (they see everything the owner sees). */
  isOwner: boolean;
  /** A platform admin acting on this brand (can switch brands from the top bar). */
  isAdmin: boolean;
  /** proan = members, points and levels live in the PROAN POS; native = this platform's own engine. */
  dataSource: DataSource;
  isNative: boolean;
  /** The brand's plan includes this module (pages of other modules are hidden; the API answers MODULE_OFF). */
  hasModule: (m: ModuleKey) => boolean;
  /** Platform admin without write rights: the API refuses every change, so change buttons are hidden. */
  readOnly: boolean;
  /** Shops for pickers; staff limited to some shops only get theirs. */
  stores: PortalMe['stores'];
  /** The user may only work at some shops, so sales and payments must name one of them. */
  shopLimited: boolean;
  logout: () => Promise<void>;
}

export const DATA_SOURCE_LABEL: Record<DataSource, string> = {
  proan: 'PROAN POS',
  native: '本系統會員資料庫',
};

export const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const s = useContext(SessionContext);
  if (!s) throw new Error('useSession outside the signed-in layout');
  return s;
}

/** Name of a shop by id (StoreInfo.id), for tables; `fallback` is the name stored with older records. */
export function useShopName() {
  const { stores, settings, defaultLang } = useSession();
  return (id: string | null | undefined, fallback?: string | null): string => {
    if (id) {
      const s = stores.find((x) => x.id === id) ?? settings?.profile.stores.find((x) => x.id === id);
      if (s) return pickText(s.name, defaultLang) || id;
    }
    return fallback || (id ? id : '—');
  };
}

export function orderLangs(enabled: readonly Lang[], defaultLang: Lang): Lang[] {
  const ordered = LANGS.filter((l) => enabled.includes(l));
  if (!ordered.includes(defaultLang)) return ordered.length ? ordered : [defaultLang];
  return [defaultLang, ...ordered.filter((l) => l !== defaultLang)];
}

export const ROLE_LABEL: Record<string, string> = {
  brand_owner: '品牌負責人',
  brand_staff: '職員',
  platform_admin: '平台管理員',
};
